import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { User } from '../api/auth';
import {
  getNotionPages,
  createNotionPage,
  updateNotionPage,
  deleteNotionPage,
  getNotionAssignments,
  assignNotionPage,
  unassignNotionPage,
  NotionPage,
  NotionAssignment,
  syncMatchesByDateToNotion,
  getNotionSyncStatus,
} from '../api';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import { useAdminList } from '../hooks/useAdminList';

interface AdminNotionTabProps {
  currentUser: User;
  admins: (User & { is_active?: boolean; created_at?: string })[];
}

const AdminNotionTab: React.FC<AdminNotionTabProps> = ({ currentUser, admins }) => {
  // Notion 페이지 관리 상태
  const [notionPages, setNotionPages] = useState<NotionPage[]>([]);
  const [notionAssignments, setNotionAssignments] = useState<NotionAssignment[]>([]);
  const [notionLoading, setNotionLoading] = useState(false);
  const [showNotionPageModal, setShowNotionPageModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingNotionPage, setEditingNotionPage] = useState<NotionPage | null>(null);
  const [notionFormData, setNotionFormData] = useState({
    page_name: '',
    notion_token: '',
    database_id: '',
    description: ''
  });
  const [assignFormData, setAssignFormData] = useState({
    admin_id: 0,
    page_id: 0
  });

  // Notion 하위 탭 상태
  const [notionSubTab, setNotionSubTab] = useState<'pages' | 'upload'>('pages');

  // Notion Sync (업로드) 관련 상태
  const { adminList } = useAdminList();
  const [notionSyncDateFrom, setNotionSyncDateFrom] = useState('');
  const [notionSyncDateTo, setNotionSyncDateTo] = useState('');
  const [notionSyncSport, setNotionSyncSport] = useState('');
  const [notionSyncAiProvider, setNotionSyncAiProvider] = useState('');
  const [notionSyncing, setNotionSyncing] = useState(false);
  const [notionSyncStatus, setNotionSyncStatus] = useState<any>(null);
  const [notionSyncAdminId, setNotionSyncAdminId] = useState<number | undefined>(currentUser?.admin_id);
  // 업로드 현황 페이지네이션
  const [notionSyncPage, setNotionSyncPage] = useState(1);
  const NOTION_SYNC_PAGE_SIZE = 10;

  useEffect(() => {
    loadNotionPages();
    loadNotionAssignments();
  }, []);

  const loadNotionPages = async () => {
    setNotionLoading(true);
    try {
      const data = await getNotionPages(true);
      setNotionPages(data.data || []);
    } catch (error: any) {
      console.error('노션 페이지 조회 실패:', error);
      toast.error('Notion 페이지 목록을 불러오는데 실패했음.');
    } finally {
      setNotionLoading(false);
    }
  };

  const loadNotionAssignments = async () => {
    try {
      const data = await getNotionAssignments();
      setNotionAssignments(data.data || []);
    } catch (error: any) {
      console.error('노션 할당 조회 실패:', error);
    }
  };

  const handleOpenNotionPageModal = (page?: NotionPage) => {
    if (page) {
      setEditingNotionPage(page);
      setNotionFormData({
        page_name: page.page_name,
        notion_token: '',
        database_id: page.database_id,
        description: page.description || ''
      });
    } else {
      setEditingNotionPage(null);
      setNotionFormData({
        page_name: '',
        notion_token: '',
        database_id: '',
        description: ''
      });
    }
    setShowNotionPageModal(true);
  };

  const handleSaveNotionPage = async () => {
    if (!notionFormData.page_name || !notionFormData.database_id) {
      toast.error('페이지 이름과 Database ID는 필수임.');
      return;
    }

    if (!editingNotionPage && !notionFormData.notion_token) {
      toast.error('Notion 토큰은 필수임.');
      return;
    }

    setNotionLoading(true);
    try {
      if (editingNotionPage) {
        const updateData: any = {
          page_name: notionFormData.page_name,
          database_id: notionFormData.database_id,
          description: notionFormData.description || null
        };
        if (notionFormData.notion_token) {
          updateData.notion_token = notionFormData.notion_token;
        }
        await updateNotionPage(editingNotionPage.page_id, updateData);
        toast.success('Notion 페이지가 수정되었음.');
      } else {
        await createNotionPage({
          page_name: notionFormData.page_name,
          notion_token: notionFormData.notion_token,
          database_id: notionFormData.database_id,
          description: notionFormData.description || undefined
        });
        toast.success('Notion 페이지가 등록되었음.');
      }
      setShowNotionPageModal(false);
      loadNotionPages();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '저장에 실패했음.');
    } finally {
      setNotionLoading(false);
    }
  };

  const handleDeleteNotionPage = async (pageId: number, pageName: string) => {
    if (!window.confirm(`'${pageName}' 페이지를 삭제하시겠습니까?\n할당된 관리자의 연결도 함께 해제됨.`)) {
      return;
    }

    setNotionLoading(true);
    try {
      await deleteNotionPage(pageId);
      toast.success('Notion 페이지가 삭제되었음.');
      loadNotionPages();
      loadNotionAssignments();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '삭제에 실패했음.');
    } finally {
      setNotionLoading(false);
    }
  };

  const handleToggleNotionPageActive = async (page: NotionPage) => {
    setNotionLoading(true);
    try {
      await updateNotionPage(page.page_id, { is_active: !page.is_active });
      toast.success(page.is_active ? '페이지가 비활성화되었음.' : '페이지가 활성화되었음.');
      loadNotionPages();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '상태 변경에 실패했음.');
    } finally {
      setNotionLoading(false);
    }
  };

  const handleOpenAssignModal = () => {
    setAssignFormData({ admin_id: 0, page_id: 0 });
    setShowAssignModal(true);
  };

  const handleAssignNotionPage = async () => {
    if (!assignFormData.admin_id || !assignFormData.page_id) {
      toast.error('관리자와 페이지를 선택해.');
      return;
    }

    setNotionLoading(true);
    try {
      await assignNotionPage(assignFormData.admin_id, assignFormData.page_id);
      toast.success('페이지가 할당되었음.');
      setShowAssignModal(false);
      loadNotionAssignments();
      loadNotionPages();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '할당에 실패했음.');
    } finally {
      setNotionLoading(false);
    }
  };

  const handleUnassignNotionPage = async (assignment: NotionAssignment) => {
    if (!window.confirm(`'${assignment.admin_name}'의 '${assignment.page_name}' 할당을 해제하시겠습니까?`)) {
      return;
    }

    setNotionLoading(true);
    try {
      await unassignNotionPage(assignment.admin_id);
      toast.success('할당이 해제되었음.');
      loadNotionAssignments();
      loadNotionPages();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '할당 해제에 실패했음.');
    } finally {
      setNotionLoading(false);
    }
  };

  return (
    <div style={styles.envContainer}>
      {/* Notion 하위 탭 */}
      <div style={styles.subTabRow}>
        <button
          style={{
            ...styles.tabButton,
            ...(notionSubTab === 'pages' ? styles.activeTab : {}),
            padding: `${spacing[2]} ${spacing[4]}`,
            fontSize: typography.fontSize.sm
          }}
          onClick={() => setNotionSubTab('pages')}
        >
          페이지 관리
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...(notionSubTab === 'upload' ? styles.activeTab : {}),
            padding: `${spacing[2]} ${spacing[4]}`,
            fontSize: typography.fontSize.sm
          }}
          onClick={() => setNotionSubTab('upload')}
        >
          업로드 관리
        </button>
      </div>

      {/* 페이지 관리 탭 */}
      {notionSubTab === 'pages' && (
      <>
      {/* Notion 페이지 목록 */}
      <div style={styles.envSection}>
        <div style={styles.sectionHeader}>
          <h4 style={styles.envSectionTitle}>등록된 Notion 페이지</h4>
          <button
            style={styles.addButton}
            onClick={() => handleOpenNotionPageModal()}
          >
            + 페이지 추가
          </button>
        </div>
        {notionLoading ? (
          <div style={styles.emptyState}>로딩 중...</div>
        ) : notionPages.length === 0 ? (
          <div style={styles.emptyState}>등록된 Notion 페이지가 없음.</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>페이지명</th>
                  <th style={styles.th}>Database ID</th>
                  <th style={styles.th}>토큰</th>
                  <th style={styles.th}>설명</th>
                  <th style={styles.th}>상태</th>
                  <th style={styles.th}>할당수</th>
                  <th style={styles.th}>관리</th>
                </tr>
              </thead>
              <tbody>
                {notionPages.map((page) => (
                  <tr key={page.page_id}>
                    <td style={styles.td}>{page.page_name}</td>
                    <td style={styles.td}>
                      <span style={styles.monoCode}>
                        {page.database_id}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: typography.fontSize.xs, color: colors.gray[500] }}>
                        {page.notion_token}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: typography.fontSize.sm, color: colors.gray[600] }}>
                        {page.description || '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={page.is_active ? styles.statusActive : styles.statusInactive}>
                        {page.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td style={styles.td}>{page.assignment_count}</td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.editButton}
                          onClick={() => handleOpenNotionPageModal(page)}
                        >
                          수정
                        </button>
                        <button
                          style={styles.toggleButton}
                          onClick={() => handleToggleNotionPageActive(page)}
                        >
                          {page.is_active ? '비활성화' : '활성화'}
                        </button>
                        <button
                          style={styles.deleteButton}
                          onClick={() => handleDeleteNotionPage(page.page_id, page.page_name)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 할당 목록 */}
      <div style={styles.envSection}>
        <div style={styles.sectionHeader}>
          <h4 style={styles.envSectionTitle}>관리자별 페이지 할당</h4>
          <button
            style={styles.addButton}
            onClick={handleOpenAssignModal}
            disabled={notionPages.filter(p => p.is_active).length === 0}
          >
            + 할당 추가
          </button>
        </div>
        <p style={styles.hint}>
          관리자 1명당 1개의 Notion 페이지만 연결됨. 새 페이지를 할당하면 기존 연결이 대체됨.
        </p>
        {notionAssignments.length === 0 ? (
          <div style={styles.emptyState}>할당된 페이지가 없음.</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>관리자</th>
                  <th style={styles.th}>페이지명</th>
                  <th style={styles.th}>Database ID</th>
                  <th style={styles.th}>상태</th>
                  <th style={styles.th}>관리</th>
                </tr>
              </thead>
              <tbody>
                {notionAssignments.map((assignment) => (
                  <tr key={assignment.admin_id}>
                    <td style={styles.td}>
                      <strong>{assignment.admin_name || `관리자 #${assignment.admin_id}`}</strong>
                    </td>
                    <td style={styles.td}>{assignment.page_name || '-'}</td>
                    <td style={styles.td}>
                      <span style={styles.monoCode}>
                        {assignment.database_id || '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={assignment.is_active ? styles.statusActive : styles.statusInactive}>
                        {assignment.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.deleteButton}
                        onClick={() => handleUnassignNotionPage(assignment)}
                      >
                        해제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* 업로드 관리 탭 */}
      {notionSubTab === 'upload' && (
      <>
      {/* 날짜별 Notion 업로드 섹션 */}
      <div style={styles.envSection}>
        <h4 style={styles.envSectionTitle}>날짜별 Notion 업로드</h4>
        <div style={styles.envFormRow}>
          {adminList.length > 0 && (
            <div style={styles.formGroup}>
              <label style={styles.label}>대상 관리자</label>
              <select
                style={styles.select}
                value={notionSyncAdminId || ''}
                onChange={(e) => setNotionSyncAdminId(e.target.value ? Number(e.target.value) : undefined)}
              >
                {adminList.map(admin => (
                  <option key={admin.admin_id} value={admin.admin_id}>
                    {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={styles.formGroup}>
            <label style={styles.label}>시작 날짜</label>
            <input
              type="date"
              style={styles.input}
              value={notionSyncDateFrom}
              onChange={(e) => setNotionSyncDateFrom(e.target.value)}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>종료 날짜</label>
            <input
              type="date"
              style={styles.input}
              value={notionSyncDateTo}
              onChange={(e) => setNotionSyncDateTo(e.target.value)}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>종목</label>
            <select
              style={styles.select}
              value={notionSyncSport}
              onChange={(e) => setNotionSyncSport(e.target.value)}
            >
              <option value="">전체</option>
              <option value="soccer">축구</option>
              <option value="baseball">야구</option>
              <option value="basketball">농구</option>
              <option value="volleyball">배구</option>
              <option value="hockey">하키</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>AI 모델</label>
            <select
              style={styles.select}
              value={notionSyncAiProvider}
              onChange={(e) => setNotionSyncAiProvider(e.target.value)}
            >
              <option value="">전체</option>
              <option value="gemini">Gemini</option>
              <option value="claude">Claude</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>
        <div style={styles.filterToolbar}>
          <button
            onClick={async () => {
              if (!notionSyncDateFrom) {
                toast.error('시작 날짜를 선택해.');
                return;
              }
              setNotionSyncing(true);
              try {
                const startDate = new Date(notionSyncDateFrom);
                const endDate = notionSyncDateTo ? new Date(notionSyncDateTo) : startDate;
                const dates: string[] = [];
                for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                  dates.push(d.toISOString().split('T')[0].replace(/-/g, ''));
                }

                const sports = notionSyncSport ? [notionSyncSport] : ['soccer', 'baseball', 'basketball', 'volleyball', 'hockey'];
                let totalMatches = 0, totalSynced = 0, totalSkipped = 0, totalFailed = 0;

                for (const dateStr of dates) {
                  for (const sport of sports) {
                    try {
                      const result = await syncMatchesByDateToNotion(sport, dateStr, notionSyncAiProvider || undefined, false, notionSyncAdminId);
                      const data = result.data;
                      totalMatches += data.total_matches;
                      totalSynced += data.synced_count;
                      totalSkipped += data.skipped_count;
                      totalFailed += data.failed_count;
                    } catch (e) {
                      // 개별 날짜/종목 실패는 무시
                    }
                  }
                }

                toast.success(`노션 업로드 완료! 총 ${totalMatches}경기: 업로드 ${totalSynced}, 건너뜀 ${totalSkipped}, 실패 ${totalFailed}`);

                const allMatches: any[] = [];
                for (const dateStr of dates) {
                  for (const sport of sports) {
                    try {
                      const statusResult = await getNotionSyncStatus(sport, dateStr, notionSyncAdminId);
                      if (statusResult.data.matches) {
                        allMatches.push(...statusResult.data.matches.map((m: any) => ({ ...m, sport, match_date: dateStr })));
                      }
                    } catch (e) {
                      // 개별 조회 실패는 무시
                    }
                  }
                }
                const dateRange = dates.length > 1 ? `${dates[0]}~${dates[dates.length - 1]}` : dates[0];
                setNotionSyncStatus({ sport: notionSyncSport || '전체', date: dateRange, matches: allMatches });
                setNotionSyncPage(1);
              } catch (error: any) {
                toast.error(`업로드 실패: ${error.message}`);
              } finally {
                setNotionSyncing(false);
              }
            }}
            disabled={!notionSyncDateFrom || notionSyncing}
            style={styles.addButton}
          >
            {notionSyncing ? '업로드 중...' : '업로드 실행'}
          </button>
          <button
            onClick={async () => {
              if (!notionSyncDateFrom) {
                toast.error('시작 날짜를 선택해.');
                return;
              }
              setNotionSyncing(true);
              try {
                const startDate = new Date(notionSyncDateFrom);
                const endDate = notionSyncDateTo ? new Date(notionSyncDateTo) : startDate;
                const dates: string[] = [];
                for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                  dates.push(d.toISOString().split('T')[0].replace(/-/g, ''));
                }

                const sports = notionSyncSport ? [notionSyncSport] : ['soccer', 'baseball', 'basketball', 'volleyball', 'hockey'];
                const allMatches: any[] = [];

                for (const dateStr of dates) {
                  for (const sport of sports) {
                    try {
                      const result = await getNotionSyncStatus(sport, dateStr, notionSyncAdminId);
                      if (result.data.matches) {
                        allMatches.push(...result.data.matches.map((m: any) => ({ ...m, sport, match_date: dateStr })));
                      }
                    } catch (e) {
                      // 개별 조회 실패는 무시
                    }
                  }
                }

                const dateRange = dates.length > 1 ? `${dates[0]}~${dates[dates.length - 1]}` : dates[0];
                setNotionSyncStatus({ sport: notionSyncSport || '전체', date: dateRange, matches: allMatches });
                setNotionSyncPage(1);
                toast.success('업로드 상태 조회 완료');
              } catch (error: any) {
                toast.error(`상태 조회 실패: ${error.message}`);
              } finally {
                setNotionSyncing(false);
              }
            }}
            disabled={!notionSyncDateFrom || notionSyncing}
            style={styles.editButton}
          >
            상태 조회
          </button>
        </div>
      </div>
        </div>

      {/* 업로드 상태 결과 테이블 */}
      {notionSyncStatus && (
        <div style={styles.envSection}>
          <div style={styles.sectionHeaderTight}>
            <h4 style={styles.envSectionTitle}>
              업로드 상태: {notionSyncStatus.sport} ({notionSyncStatus.date}) - 총 {notionSyncStatus.matches?.length || 0}건
            </h4>
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, width: '80px'}}>날짜</th>
                  <th style={{...styles.th, width: '60px'}}>종목</th>
                  <th style={styles.th}>경기</th>
                  <th style={{...styles.th, width: '60px'}}>시간</th>
                  <th style={styles.th}>DB 예측</th>
                  <th style={{...styles.th, width: '70px'}}>노션</th>
                  <th style={styles.th}>노션 예측</th>
                  <th style={{...styles.th, width: '80px'}}>상태</th>
                </tr>
              </thead>
              <tbody>
                {notionSyncStatus.matches?.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{...styles.td, textAlign: 'center', padding: '40px'}}>
                      해당 조건에 맞는 경기가 없음.
                    </td>
                  </tr>
                ) : (
                  notionSyncStatus.matches
                    ?.slice((notionSyncPage - 1) * NOTION_SYNC_PAGE_SIZE, notionSyncPage * NOTION_SYNC_PAGE_SIZE)
                    .map((match: any, index: number) => {
                    const dbPredictions = match.db_predictions?.map((p: any) => p.ai_model) || [];
                    const notionPredictions = match.notion_predictions || [];
                    const notionPredictionModels = notionPredictions.map((p: any) => typeof p === 'string' ? p : p.ai_model);
                    const missingInNotion = dbPredictions.filter((p: string) => !notionPredictionModels.includes(p));
                    const isSynced = missingInNotion.length === 0 && match.notion_page_exists;
                    const sportNames: Record<string, string> = { soccer: '축구', baseball: '야구', basketball: '농구', volleyball: '배구', hockey: '하키' };
                    const sportName = sportNames[match.sport as string] || match.sport;
                    const displayDate = match.match_date ? `${match.match_date.slice(0,4)}-${match.match_date.slice(4,6)}-${match.match_date.slice(6,8)}` : '-';
                    return (
                      <tr key={`${match.match_id}-${index}`}>
                        <td style={styles.td}>{displayDate}</td>
                        <td style={styles.td}>{sportName}</td>
                        <td style={styles.td}>{match.home_team} vs {match.away_team}</td>
                        <td style={styles.td}>{match.match_time}</td>
                        <td style={styles.td}>{dbPredictions.length > 0 ? dbPredictions.join(', ') : '-'}</td>
                        <td style={styles.td}>
                          <span style={match.notion_page_exists ? styles.statusActive : styles.statusInactive}>
                            {match.notion_page_exists ? '있음' : '없음'}
                          </span>
                        </td>
                        <td style={styles.td}>{notionPredictionModels.length > 0 ? notionPredictionModels.join(', ') : '-'}</td>
                        <td style={styles.td}>
                          <span style={isSynced ? styles.statusActive : styles.statusInactive}>
                            {isSynced ? '업로드됨' : missingInNotion.length > 0 ? `미업로드` : '예측없음'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* 페이지네이션 */}
          {notionSyncStatus.matches && notionSyncStatus.matches.length > NOTION_SYNC_PAGE_SIZE && (
            <div style={styles.paginationRow}>
              <button
                style={styles.editButton}
                disabled={notionSyncPage === 1}
                onClick={() => setNotionSyncPage(p => p - 1)}
              >
                이전
              </button>
              <span style={styles.paginationLabel}>
                {notionSyncPage} / {Math.ceil(notionSyncStatus.matches.length / NOTION_SYNC_PAGE_SIZE)}
              </span>
              <button
                style={styles.editButton}
                disabled={notionSyncPage >= Math.ceil(notionSyncStatus.matches.length / NOTION_SYNC_PAGE_SIZE)}
                onClick={() => setNotionSyncPage(p => p + 1)}
              >
                다음
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Notion 페이지 추가/수정 모달 */}
      {showNotionPageModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowNotionPageModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {editingNotionPage ? 'Notion 페이지 수정' : 'Notion 페이지 추가'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>페이지 이름 *</label>
              <input
                type="text"
                value={notionFormData.page_name}
                onChange={(e) => setNotionFormData({ ...notionFormData, page_name: e.target.value })}
                style={styles.input}
                placeholder="예: 축구 분석 DB"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Notion Integration Token {editingNotionPage ? '' : '*'}
              </label>
              <input
                type="password"
                value={notionFormData.notion_token}
                onChange={(e) => setNotionFormData({ ...notionFormData, notion_token: e.target.value })}
                style={styles.input}
                placeholder={editingNotionPage ? '변경 시에만 입력' : 'secret_xxx...'}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Database ID *</label>
              <input
                type="text"
                value={notionFormData.database_id}
                onChange={(e) => setNotionFormData({ ...notionFormData, database_id: e.target.value })}
                style={styles.input}
                placeholder="32자리 ID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>설명</label>
              <input
                type="text"
                value={notionFormData.description}
                onChange={(e) => setNotionFormData({ ...notionFormData, description: e.target.value })}
                style={styles.input}
                placeholder="페이지 용도 설명 (선택)"
              />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowNotionPageModal(false)}>
                취소
              </button>
              <button style={styles.submitButton} onClick={handleSaveNotionPage}>
                {editingNotionPage ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notion 페이지 할당 모달 */}
      {showAssignModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowAssignModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Notion 페이지 할당</h3>
            <p style={styles.hint}>
              관리자 1명당 1개의 페이지만 연결됨. 기존 연결이 있으면 새 페이지로 대체됨.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>관리자 선택 *</label>
              <select
                value={assignFormData.admin_id}
                onChange={(e) => setAssignFormData({ ...assignFormData, admin_id: Number(e.target.value) })}
                style={styles.select}
              >
                <option value={0}>-- 관리자 선택 --</option>
                {admins.map((admin) => (
                  <option key={admin.admin_id} value={admin.admin_id}>
                    {admin.display_name} ({admin.username})
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Notion 페이지 선택 *</label>
              <select
                value={assignFormData.page_id}
                onChange={(e) => setAssignFormData({ ...assignFormData, page_id: Number(e.target.value) })}
                style={styles.select}
              >
                <option value={0}>-- 페이지 선택 --</option>
                {notionPages.filter(p => p.is_active).map((page) => (
                  <option key={page.page_id} value={page.page_id}>
                    {page.page_name} ({page.database_id.substring(0, 8)}...)
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowAssignModal(false)}>
                취소
              </button>
              <button style={styles.submitButton} onClick={handleAssignNotionPage}>
                할당
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  envContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[5],
  },
  envSection: {
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
    padding: spacing[6],
  },
  // 공통 레이아웃 헬퍼
  subTabRow: {
    display: 'flex',
    gap: spacing[2],
    marginBottom: spacing[4],
    borderBottom: `1px solid ${colors.gray[200]}`,
    paddingBottom: spacing[2],
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[4],
  },
  sectionHeaderTight: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[3],
  },
  monoCode: {
    fontSize: typography.fontSize.xs,
    fontFamily: 'monospace',
  },
  hint: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    marginBottom: spacing[4],
  },
  filterToolbar: {
    display: 'flex',
    gap: spacing[2],
    marginBottom: spacing[6],
  },
  paginationRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: spacing[2],
    marginTop: spacing[3],
  },
  paginationLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: typography.fontSize.sm,
  },
  envSectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginTop: 0,
    marginBottom: spacing[5],
  },
  envFormRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[5],
    alignItems: 'flex-end',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  label: {
    ...commonStyles.label,
    minWidth: 'auto',
  },
  input: {
    ...commonStyles.input,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    ...commonStyles.select,
    width: '100%',
  },
  addButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
  editButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  toggleButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  deleteButton: {
    ...commonStyles.button,
    ...commonStyles.buttonDanger,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  tableContainer: {
    ...commonStyles.tableContainer,
  },
  table: {
    ...commonStyles.table,
  },
  th: {
    ...commonStyles.th,
  },
  td: {
    ...commonStyles.td,
  },
  emptyState: {
    ...commonStyles.emptyState,
  },
  statusActive: {
    color: colors.status.success,
    fontWeight: typography.fontWeight.medium,
  },
  statusInactive: {
    color: colors.status.error,
    fontWeight: typography.fontWeight.medium,
  },
  actionButtons: {
    display: 'flex',
    gap: spacing[2],
  },
  tabButton: {
    ...commonStyles.tabButton,
  },
  activeTab: {
    ...commonStyles.tabButtonActive,
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.white,
    padding: spacing[8],
    minWidth: '400px',
    maxWidth: '500px',
    border: `1px solid ${colors.gray[200]}`,
  },
  modalTitle: {
    ...commonStyles.sectionTitle,
    marginBottom: spacing[6],
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
    marginTop: spacing[6],
  },
  cancelButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
  },
  submitButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
};

export default AdminNotionTab;
